<template>
  <div>
    <el-collapse ref="departmentsCollapse" @change="collapseChanged">
      <el-collapse-item v-for="department in departments" :key="department.id" :name="department.name" :id="'department_' + department.id" :ref="'department_' + department.id">
        <template slot="title">
          <div class="collapse-item-header" style="width: 100%">
            {{department.name}}
            <div class="pull-right chat-icon relative">
              <img @click.stop="downloadExcel(department.id)" v-if="hasJobs(department.id)" src="static/img/icon-excel.jpg" title="Export Jobs to Excel" class="m-left-12"/>
              <img @click.stop="viewFiles(department.name)" v-if="canReadFiles" src="static/img/icon-folder.png" title="Department Files" class="m-left-12" />
              <img @click.stop="viewTaskSearchResults(department.id)" src="static/img/icon-tasks.png" title="Department Tasks" class="m-left-8" />
              <img @click.stop="openComments(department.id)" :id="'_' + department.id" :src="iconColour(getCommentCount(getParentEntityIdHash(department.id)))" :ref="getParentEntityIdHash(department.id)" title="Department Comments" class="m-left-12" />
              <div class="button-count button-count-departments" v-if="getCommentCount(getParentEntityIdHash(department.id)) > 0" >{{ getCommentCount(getParentEntityIdHash(department.id)) }}</div>
              <img @click.stop="handleShowPreferenceModal" src="static/img/gear-icon.png" title="Configure Optional Columns" class="m-left-12" style="width: 26px; height: 26px;"/>
            </div>
          </div>
        </template>
        <department :department="department" :project-id="projectId" />
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
  import Department from './Department'
  import {mapGetters} from 'vuex'
  import {fileSystemPath, getParentEntityIdProjectDepartmentHash, objectById,} from 'src/utilities/utils'
  import Constants from 'src/utilities/constants'
  import Permissions from 'src/utilities/permissions'
  import {refreshJobs} from '@/utilities/jobHelper'
  import _ from 'lodash'

  export default {
    props: ['projectId'],
    components: {
      Department
    },
    computed: {
      ...mapGetters({
        isExpandAllDepartments: 'projects/edit/expandAllDepartments',
        departments: 'projects/edit/departments',
        allDepartments: 'departments/results'
      }),
      canReadFiles() {
        return this.$can('read', Permissions.FILES)
      }
    },
    methods: {
      iconColour(commentCount) {
        return commentCount > 0 ? 'static/img/comment-red.png' : 'static/img/comment-blue.png'
      },
      getCommentCount(parentEntityIdHash) {
        return this.$store.getters['comments/count'](parentEntityIdHash)
      },
      handleShowPreferenceModal() {
        const payload = {
          title: 'Jobs By Department',
          preferenceType: Constants.USER_PREFERENCE_TYPES.JOBS_BY_DEPARTMENT
        }
        this.$eventHub.$emit('show-modal-1', 'Search and Sort Preferences', payload, '900px')
      },
      hasJobs(departmentId) {
        const jobs = this.$store.getters['jobs/byDepartment'](departmentId)
        return Array.isArray(jobs) && jobs.length > 0
      },
      viewFiles(departmentName) {
        const projectId = this.$store.getters['projects/edit/projectId']
        const projectName = this.$store.getters['projects/edit/details/name']

        const path = fileSystemPath(projectName, projectId, departmentName)
        this.$eventHub.$emit('show-modal-1', 'File Browser', path, '800px')
      },
      downloadExcel(departmentId) {
        const jobs = this.$store.get('jobs/byDepartment', departmentId)
        this.$store.dispatch('projects/edit/resultsToExcel', jobs)
      },
      viewTaskSearchResults(departmentId) {
        const projectName = this.$store.getters['projects/edit/details/name']
        this.$store.commit('tasks/search/resetResults')
        this.$store.commit('tasks/search/resetQuery')
        this.$router.push({path: `/tasks/search?departmentId=${departmentId}&search=${encodeURIComponent(projectName)}`})
      },
      getParentEntityIdHash(departmentId) {
        return getParentEntityIdProjectDepartmentHash(this.projectId, departmentId)
      },
      openComments(departmentId) {
        this.$eventHub.$emit('show-modal-1', 'Comments', this.getParentEntityIdWithSubheading(departmentId))
      },
      getParentEntityId(departmentId) {
        return {
          projectId: this.projectId,
          departmentId: departmentId
        }
      },
      getParentEntityIdWithSubheading(departmentId) {
        const department = objectById(this.departments, departmentId)
        return {
          parentId: this.getParentEntityId(departmentId),
          subheading: `<strong>Project</strong>: ${this.$store.getters['projects/edit/details/name']}<br/><strong>Department</strong>: ${department.name}`
        }
      },
      watchForOpenCommentsQuerystring() {
        setTimeout(() => {
          const qs = this.$route.query
          if (qs && qs.hasOwnProperty('cp') && this.$refs.hasOwnProperty(qs.cp)) {
            let departmentId = this.$refs[qs.cp][0].id // ids can't start with a number, prefix with underscore
            this.openComments(departmentId.substr(1, departmentId.length)) // trim off preceding underscore
          }
        }, Constants.TIME.MILLIS.ONE_SECOND)
      },
      maybeScrollToJob() {
        const qs = this.$route.query
        if (qs && qs.jobId) {
          const job = this.$store.getters['jobs/byId'](qs.jobId)
          if (job) {
            const department = objectById(this.departments, job.departmentId)
            if (department) {
              this.$refs.departmentsCollapse.setActiveNames(department.name)
              const refId = 'department_' + job.departmentId
              if (this.$refs[refId]) {
                setTimeout(() => {
                  this.$scrollTo('#' + refId)
                }, Constants.TIME.MILLIS.HALF_SECOND)
              }
            }
          }
        }
      },
      expandAllDepartments() {
        const departmentNames = this.departments.map(department => department.name)
        this.$refs.departmentsCollapse.setActiveNames(departmentNames)
      },
      maybeOpenDepartmentCollapseItem() {
        if (this.isExpandAllDepartments) {
          this.expandAllDepartments()
          return
        }

        const currentUser = this.$store.getters['security/user']
        if (_.get(currentUser, 'preferences.global.defaultDepartment')) {
          const departmentId = currentUser.preferences.global.defaultDepartment
          const department = objectById(this.allDepartments, departmentId)
          if (department) {
            this.$refs.departmentsCollapse.setActiveNames(department.name)
          }
        }
      },
      collapseChanged(event) {
        if (event.length > 0) {
          this.$eventHub.$emit('jobsExpanded')
        }

        if (event.length < this.departments.length) {
          this.$store.set('projects/edit/setExpandAllDepartments', false)
        }
      },
      refreshProjectJobs(job) {
        refreshJobs(this, job)
      }
    },
    async beforeMount() {
      if (!this.$store.get('users/allUserPreferencesLoaded')) {
        await this.$store.cache.dispatch('users/fetchAllPreferences')
      }
    },
    mounted() {
      this.watchForOpenCommentsQuerystring()
      this.maybeOpenDepartmentCollapseItem()

      this.$eventHub.$once('projectJobsLoaded', this.maybeScrollToJob)
      this.$eventHub.$on('refreshProjectJobs', this.refreshProjectJobs)
      this.$eventHub.$on('collapseProjectDepartments', this.maybeOpenDepartmentCollapseItem)
    },
    destroyed() {
      this.$eventHub.$off('projectJobsLoaded')
      this.$eventHub.$off('refreshProjectJobs')
      this.$eventHub.$off('collapseProjectDepartments')
    },
    watch: {
      '$route.query'() {
        this.watchForOpenCommentsQuerystring()
      },
      isExpandAllDepartments(expandAllDepartments) {
        if (expandAllDepartments) {
          this.expandAllDepartments()
        }
      },
      departments() {
        if (this.isExpandAllDepartments) {
          this.expandAllDepartments()
        }
      }
    }
  }
</script>
<style scoped>
  .button-count-departments {
    position: absolute;
    right: 25px;
    bottom: 31px;
  }
</style>
